/**
 * Chi compra i biglietti delle navette per eventi.
 *
 * È un'anagrafica a sé, e deve restare tale. Nel gestionale ci sono
 * già due popolazioni che si chiamano « clienti » e non c'entrano con
 * questa: gli studenti abbonati del trasporto scolastico, che hanno un
 * account per le famiglie, e i committenti dei noleggi, che stanno nei
 * Preventivi. Chi compra un posto per un concerto non è né l'uno né
 * l'altro, non ha un account nel gestionale e non deve finire nei loro
 * elenchi.
 */
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api, { getAll } from '../../services/api';
import { notify } from '../../utils/notifications';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import { euro, dataIT, dataOraIT } from './Eventi';
import { STATI_ORDINE } from './OrdiniEventi';
import {
  Search, Users, Mail, Phone, Ticket, Euro, KeyRound, X, Trash2,
  Download, UserCheck,
} from 'lucide-react';

const ClientiEventi = () => {
  const { utente } = authStore();
  const modificabile = puoModificare(utente);

  const [clienti, setClienti] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [ricerca, setRicerca] = useState('');
  const [soloConAccesso, setSoloConAccesso] = useState(false);
  const [aperto, setAperto] = useState(null);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      setClienti(await getAll('/navette/clienti/'));
    } catch (errore) {
      console.error('Errore caricamento clienti:', errore);
      notify.error('Errore nel caricamento dei clienti');
    } finally {
      setCaricamento(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const elimina = async (cliente, e) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare la scheda di ${cliente.nome_completo}?`)) return;
    try {
      await api.delete(`/navette/clienti/${cliente.id}/`);
      notify.success('Scheda eliminata');
      carica();
    } catch (errore) {
      notify.error(errore.response?.data?.errore || 'Eliminazione non riuscita');
    }
  };

  const testo = ricerca.trim().toLowerCase();
  const filtrati = clienti.filter((c) => {
    if (soloConAccesso && !c.ha_accesso) return false;
    if (!testo) return true;
    return [c.nome_completo, c.email, c.telefono, c.codice_fiscale]
      .some((v) => (v || '').toLowerCase().includes(testo));
  });

  const conAccesso = clienti.filter((c) => c.ha_accesso).length;
  const incassato = clienti.reduce((s, c) => s + Number(c.speso || 0), 0);
  const postiVenduti = clienti.reduce((s, c) => s + (c.posti_comprati || 0), 0);

  const scarica = () => {
    if (filtrati.length === 0) return notify.info('Non c\'è niente da scaricare.');
    const righe = [[
      'Cognome', 'Nome', 'Email', 'Telefono', 'Codice fiscale',
      'Ordini', 'Confermati', 'Posti', 'Speso', 'Accesso', 'Ultimo ordine',
    ]];
    filtrati.forEach((c) => {
      righe.push([
        c.cognome, c.nome, c.email, c.telefono, c.codice_fiscale,
        c.ordini_totali, c.ordini_confermati, c.posti_comprati,
        String(c.speso || '0.00').replace('.', ','),
        c.ha_accesso ? 'sì' : 'no',
        c.ultimo_ordine ? dataIT(c.ultimo_ordine) : '',
      ]);
    });
    // Punto e virgola e BOM: Excel in italiano apre il file senza
    // chiedere nulla e con gli accenti giusti.
    const csv = '﻿' + righe
      .map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `clienti-navette-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-start gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Clienti</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Chi ha comprato un biglietto dal sito. Anagrafica separata da quella
              del trasporto scolastico e dai committenti dei noleggi.
            </p>
          </div>
          {clienti.length > 0 && (
            <button
              onClick={scarica}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:border-gray-900 transition text-sm font-medium"
            >
              <Download className="w-4 h-4" />
              Scarica l'elenco
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <Riquadro icona={Users} etichetta="Clienti" valore={clienti.length} />
          <button
            onClick={() => setSoloConAccesso(!soloConAccesso)}
            className={`text-left bg-white rounded-xl border p-4 transition
              ${soloConAccesso ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200 hover:border-gray-400'}`}
          >
            <div className="flex items-center gap-2 text-gray-500 text-xs font-medium mb-1">
              <KeyRound className="w-3.5 h-3.5" />
              Con accesso attivo
            </div>
            <div className="text-2xl font-bold text-gray-900">{conAccesso}</div>
          </button>
          <Riquadro icona={Ticket} etichetta="Posti venduti" valore={postiVenduti} />
          <Riquadro icona={Euro} etichetta="Incassato" valore={euro(incassato)} />
        </div>

        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
            placeholder="Cerca per nome, email, telefono o codice fiscale"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-900"
          />
        </div>

        {caricamento ? (
          <div className="py-16 text-center text-gray-400">Carico…</div>
        ) : filtrati.length === 0 ? (
          <div className="py-16 text-center">
            <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {clienti.length === 0
                ? 'Nessun cliente: la scheda si crea da sola al primo acquisto dal sito.'
                : 'Nessun cliente con questi filtri.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
            {filtrati.map((cliente) => (
              <div
                key={cliente.id}
                onClick={() => setAperto(cliente)}
                className="flex items-center gap-4 p-4 hover:bg-gray-50 cursor-pointer transition"
              >
                <div className="w-9 h-9 shrink-0 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold flex items-center justify-center">
                  {(cliente.nome[0] || '') + (cliente.cognome[0] || '')}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 truncate">
                      {cliente.nome_completo}
                    </span>
                    {cliente.ha_accesso && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700">
                        <KeyRound className="w-3 h-3" />
                        Accesso attivo
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mt-0.5 truncate">
                    {[cliente.email, cliente.telefono].filter(Boolean).join(' · ')}
                  </div>
                </div>

                <div className="hidden sm:block text-right shrink-0">
                  <div className="text-sm font-semibold text-gray-900">
                    {euro(cliente.speso)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {cliente.ordini_confermati} ordin{cliente.ordini_confermati === 1 ? 'e' : 'i'}
                    {cliente.posti_comprati > 0 && ` · ${cliente.posti_comprati} post${cliente.posti_comprati === 1 ? 'o' : 'i'}`}
                  </div>
                </div>

                {modificabile && cliente.ordini_totali === 0 && (
                  <button
                    onClick={(e) => elimina(cliente, e)}
                    className="p-2 text-gray-300 hover:text-red-600 transition shrink-0"
                    aria-label={`Elimina ${cliente.nome_completo}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {aperto && (
        <PannelloCliente
          cliente={aperto}
          onChiudi={() => setAperto(null)}
        />
      )}
    </AdminLayout>
  );
};

const Riquadro = ({ icona: Icona, etichetta, valore }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="flex items-center gap-2 text-gray-500 text-xs font-medium mb-1">
      <Icona className="w-3.5 h-3.5" />
      {etichetta}
    </div>
    <div className="text-2xl font-bold text-gray-900">{valore}</div>
  </div>
);

const PannelloCliente = ({ cliente, onChiudi }) => {
  const [ordini, setOrdini] = useState(null);

  useEffect(() => {
    getAll('/navette/ordini/', { cliente: cliente.id })
      .then(setOrdini)
      .catch(() => { notify.error('Non riesco a caricare gli ordini'); setOrdini([]); });
  }, [cliente.id]);

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4"
      onClick={onChiudi}
    >
      <div
        className="bg-white w-full sm:max-w-2xl sm:rounded-xl rounded-t-xl max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900">{cliente.nome_completo}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Cliente dal {dataIT(cliente.data_creazione?.slice(0, 10))}
            </p>
          </div>
          <button
            onClick={onChiudi}
            className="p-1.5 text-gray-400 hover:text-gray-900 transition shrink-0"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <Dato icona={Mail} etichetta="Email" valore={cliente.email} />
            <Dato icona={Phone} etichetta="Telefono" valore={cliente.telefono} />
            {cliente.codice_fiscale && (
              <Dato icona={UserCheck} etichetta="Codice fiscale" valore={cliente.codice_fiscale} />
            )}
            <Dato
              icona={KeyRound}
              etichetta="Accesso al sito"
              valore={cliente.ha_accesso
                ? (cliente.ultimo_accesso
                  ? `Attivo · ultimo ingresso ${dataOraIT(cliente.ultimo_accesso)}`
                  : 'Attivo')
                : 'Mai attivato: compra come ospite'}
            />
          </div>

          {/* Il perché della riga sopra, per chi non ha seguito la costruzione */}
          {!cliente.ha_accesso && (
            <p className="text-xs text-gray-500 -mt-2">
              L'accesso è facoltativo: l'invito a sceglierne uno parte con i
              biglietti, e chi lo ignora compra lo stesso.
            </p>
          )}

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">
              Ordini {ordini ? `(${ordini.length})` : ''}
            </h3>
            {ordini === null ? (
              <p className="text-sm text-gray-400 py-4">Carico…</p>
            ) : ordini.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">Nessun ordine.</p>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {ordini.map((o) => {
                  const stato = STATI_ORDINE[o.stato] || STATI_ORDINE.IN_SOSPESO;
                  return (
                    <div key={o.id} className="p-3 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {o.evento_titolo}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {o.codice} · {dataIT(o.evento_data)} · {o.navetta_etichetta}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-semibold text-gray-900">
                          {euro(o.totale)}
                        </div>
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${stato.classe}`}>
                          {stato.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {cliente.note && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Note interne</h3>
              <p className="text-sm text-gray-600 whitespace-pre-line">{cliente.note}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Dato = ({ icona: Icona, etichetta, valore }) => (
  <div>
    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-0.5">
      <Icona className="w-3.5 h-3.5" />
      {etichetta}
    </div>
    <div className="text-sm text-gray-900 break-words">{valore || '—'}</div>
  </div>
);

export default ClientiEventi;
